import { useState } from 'react'
import { Link, useRouterState } from '@tanstack/react-router'
import { useAuth } from '@/features/auth'
import { useAlertBadgeCount } from '@/features/insights/hooks/use-insights'
import { cn } from '@/lib/utils'
import {
  LayoutDashboard,
  Users,
  FileText,
  Settings,
  LogOut,
  ChevronLeft,
  Activity,
  Shield,
  Loader2,
  CalendarDays,
} from 'lucide-react'
import { Logo } from '@/components/Logo'

interface SidebarProps {
  isCollapsed: boolean
  onToggle: () => void
  onNavigate?: () => void
}

export function Sidebar({ isCollapsed, onToggle, onNavigate }: SidebarProps) {
  const router = useRouterState()
  const currentPath = router.location.pathname
  const { isAdmin, signOut, loading } = useAuth()
  const [isLoggingOut, setIsLoggingOut] = useState(false)

  const navigation = [
    {
      name: 'Dashboard',
      href: '/dashboard',
      icon: LayoutDashboard,
      current: currentPath === '/dashboard',
    },
    {
      name: 'Pacientes',
      href: '/patients',
      icon: Users,
      current: currentPath.startsWith('/patients'),
    },
    {
      name: 'Agenda',
      href: '/schedule',
      icon: CalendarDays,
      current: currentPath.startsWith('/schedule'),
    },
    {
      name: 'Relatos',
      href: '/reports',
      icon: FileText,
      current: currentPath === '/reports',
    },
    {
      name: 'Alertas',
      href: '/insights',
      icon: Activity,
      current: currentPath === '/insights',
    },
    // Link administrativo - so aparece para admins
    ...(isAdmin
      ? [
          {
            name: 'Administração',
            href: '/admin',
            icon: Shield,
            current: currentPath.startsWith('/admin'),
          },
        ]
      : []),
    {
      name: 'Configurações',
      href: '/settings',
      icon: Settings,
      current: currentPath === '/settings',
    },
  ]

  const { data: alertBadgeCount } = useAlertBadgeCount()

  const handleLogout = async () => {
    setIsLoggingOut(true)
    try {
      await signOut()
    } catch (error) {
      console.error('Erro no logout:', error)
    } finally {
      setIsLoggingOut(false)
    }
  }

  return (
    <div
      className={cn(
        'sidebar h-screen flex flex-col transition-all duration-300 shadow-sm',
        isCollapsed ? 'w-16' : 'w-64'
      )}
    >
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gradient-to-r from-teal-500 to-teal-600">
        {!isCollapsed && (
          <div className="flex items-center space-x-2">
            <Logo size={32} variant="icon-only" />
            <div className="space-y-0.5">
              <h1 className="text-sm font-semibold text-white font-poppins leading-tight">
                SecBrain
              </h1>
              <p className="text-xs text-teal-50">Neurodivergente</p>
            </div>
          </div>
        )}

        <button
          onClick={onToggle}
          className="p-1.5 rounded-lg bg-white/20 hover:bg-white/30 transition-colors"
        >
          <ChevronLeft
            className={cn(
              'w-4 h-4 text-white transition-transform',
              isCollapsed && 'rotate-180'
            )}
          />
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-3 py-4 space-y-1">
        {navigation.map((item) => {
          const Icon = item.icon
          return (
            <Link
              key={item.name}
              to={item.href}
              onClick={onNavigate}
              className={cn(
                'w-full flex items-center px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 relative',
                item.current
                  ? 'bg-teal-50 text-teal-700 border border-teal-200'
                  : 'text-gray-700 hover:bg-gray-50 hover:text-teal-700',
                isCollapsed ? 'justify-center' : 'justify-start'
              )}
              title={isCollapsed ? item.name : undefined}
            >
              <Icon
                className={cn(
                  'w-5 h-5 flex-shrink-0',
                  !isCollapsed && 'mr-3',
                  item.current && 'text-teal-600'
                )}
              />
              {!isCollapsed && (
                <span className="animate-fade-in flex-1">{item.name}</span>
              )}
              {item.href === '/insights' && (alertBadgeCount ?? 0) > 0 && (
                <span
                  className={cn(
                    'flex items-center justify-center text-xs font-bold text-white bg-red-500 rounded-full',
                    isCollapsed ? 'absolute -top-1 -right-1 w-4 h-4 text-[10px]' : 'ml-auto w-5 h-5'
                  )}
                >
                  {alertBadgeCount! > 9 ? '9+' : alertBadgeCount}
                </span>
              )}
            </Link>
          )
        })}
      </nav>

      {/* Footer */}
      <div className="p-3 border-t border-gray-200">
        <button
          onClick={handleLogout}
          disabled={isLoggingOut || loading}
          className={cn(
            'w-full flex items-center px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 hover:bg-red-50 hover:text-red-700 border border-transparent hover:border-red-200',
            isCollapsed ? 'justify-center' : 'justify-start'
          )}
          title={isCollapsed ? 'Sair' : undefined}
        >
          {isLoggingOut ? (
            <Loader2
              className={cn('w-5 h-5 flex-shrink-0 animate-spin', !isCollapsed && 'mr-3')}
            />
          ) : (
            <LogOut className={cn('w-5 h-5 flex-shrink-0', !isCollapsed && 'mr-3')} />
          )}
          {!isCollapsed && (
            <span className="animate-fade-in">
              {isLoggingOut ? 'Saindo...' : 'Sair'}
            </span>
          )}
        </button>
      </div>
    </div>
  )
}
